import { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { canEnterEdit } from '../../lib/permissions';
import { canAiAdminHere } from '../../lib/capabilities';
import { fetchVersion } from '../../api/meta';
import { BUILD_VERSION } from '../../version';
import { LEFT_CONTROLS, LEFT_PRIORITY } from './topbarControls';
import { TopbarOverflowMenu } from './TopbarOverflowMenu';

/**
 * The left-hand group of the top bar: the brand, the version trigger, and the edit-mode
 * actions.
 *
 * This retires the last vanilla seam around the About panel. `#btn-about` used to be vanilla
 * markup whose click handler dynamically imported the React bundle, called `mountAll()`
 * in case the click had beaten it, and then reached the panel through
 * `window.InfoStationReact.about`. All of that existed because the trigger and the panel were
 * on opposite sides of the migration. They are not any more, so the trigger reads and writes
 * the same store the panel does, and `aria-expanded` is derived from it rather than being
 * written into the DOM from the panel's effect.
 *
 * THE EDIT-MODE BUTTONS are conditionally rendered rather than display:none-d. The vanilla
 * `setEditing()` set `style.display` on six of them by hand, which is six chances to forget
 * one; each registry control's `visible()` gates on `editing` in one place now
 * (`topbarControls.tsx`, plan 1.3 — the buttons render FROM the registry, markup unchanged).
 * What has NOT changed is where the state lives: `setEditing()` still owns the cursor, the
 * hint, the sidebar footer, the body class and the presence broadcast, and is still reached
 * through an action.
 */

const VERSION_LABEL = (v: string) => `V${v}`;

export function TopbarLeft({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [aboutOpen, setAboutOpen] = useState(false);

  // The build's own version, corrected from the server once it answers. Same behaviour the
  // vanilla brand had, and for the same reason: a cached page can be older than the server it
  // is talking to, and that number is what somebody quotes in a support request. The request
  // is shared with the About panel through api/meta's promise cache, so it is one round trip.
  const [version, setVersion] = useState(BUILD_VERSION);
  useEffect(() => {
    let live = true;
    void fetchVersion().then(
      (v) => {
        if (live && v.version) setVersion(v.version);
      },
      () => {
        /* the built-in version stays; it is the best we have */
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const editing = s.editing;
  // Any participant brick (V0.37): there is something in edit mode for every one of them,
  // and each affordance inside the mode carries its own precise gate.
  const showEdit = canEnterEdit(s.perm);
  const showAi = editing && canAiAdminHere(s.perm, s.caps);

  useLayoutEffect(() => {
    host.classList.add('tb-left');
  }, [host]);

  // The buttons this group shows change its width, and the search box's visibility is decided
  // by what is left over. Gated on `booted` for the same reason as the right group: this
  // mounts before boot() has registered any action.
  useLayoutEffect(() => {
    if (!s.booted) return;
    callAction('fitTopbar');
  }, [s.booted, editing, showEdit, showAi, version]);

  // The About panel lives in its own root, so its open state is shared through the handle it
  // publishes rather than through props. Subscribing keeps `aria-expanded` honest even when
  // something else closes the panel — Escape, or a click outside.
  useEffect(() => {
    const w = window as unknown as {
      InfoStationReact?: { about?: { subscribe?: (fn: (open: boolean) => void) => () => void } };
    };
    return w.InfoStationReact?.about?.subscribe?.(setAboutOpen);
  }, []);

  const toggleAbout = () => {
    const w = window as unknown as { InfoStationReact?: { about?: { toggle: () => void } } };
    w.InfoStationReact?.about?.toggle();
  };

  // A control is inline OR in the "…" menu, never both (plan 1.3). The menu lists its side's
  // overflowed controls in first-out priority order — the order the plan names them in.
  const ovf = new Set(s.topbarOverflow);
  const menuControls = LEFT_PRIORITY.map((id) => LEFT_CONTROLS.find((c) => c.id === id)!).filter(
    (c) => ovf.has(c.id) && c.visible(s),
  );

  return (
    <>
      <div className="brand">
        <div className="mark">C</div>
        <div>
          CONIGON Suite
          <button
            type="button"
            className="brandver"
            id="btn-about"
            title="Version, deployment and what's new"
            aria-expanded={aboutOpen}
            onClick={toggleAbout}
          >
            InfoStation · <span id="brand-version">{VERSION_LABEL(version)}</span>
          </button>
        </div>
      </div>

      {showEdit ? (
        <button
          className={editing ? 'btn editswitch on' : 'btn editswitch'}
          id="btn-edit"
          aria-pressed={editing}
          onClick={() => callAction('toggleEditing')}
        >
          <span className="dot-ind" />
          <svg className="ico">
            <use href="#i-edit" />
          </svg>
          <span id="edit-label">{editing ? 'Editing' : 'View only'}</span>
        </button>
      ) : null}

      {LEFT_CONTROLS.map((c) =>
        c.visible(s) && !ovf.has(c.id) ? <Fragment key={c.id}>{c.inline(s)}</Fragment> : null,
      )}

      {/* At the right end of the group (user decision). Every left control is edit-mode-only,
          so this menu can only ever exist in edit mode — and only when something overflowed. */}
      <TopbarOverflowMenu side="left" controls={menuControls} />
    </>
  );
}
